import React, { useState, useRef, useEffect, ChangeEvent } from "react";
import { IoSearch } from "react-icons/io5";
import { choose, fetchAssetTypes, fetchSecurities, resetSecurities, selectAssetTypeIdsAcoes, selectSecurities, selectSecurityChosen } from "./symbol-search-bar-slice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../features/authentication/authentication-slice";
import { instanceToPlain, plainToInstance } from "class-transformer";
import Security from "../../api/models/fixProtocol/instrument/security";

interface SymbolSearchBarProps {
  onSymbolChange: (symbol: string) => void
}

const SymbolSearchBar: React.FC<SymbolSearchBarProps> = (props: SymbolSearchBarProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [symbolChangeTimer, setSymbolChangeTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const securityFilterRef = useRef<HTMLInputElement | null>(null);
  const securityListRef = useRef<HTMLUListElement | null>(null);

  // Reducer
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const assetTypeIds = useAppSelector(selectAssetTypeIdsAcoes);
  const securities = plainToInstance(Security, useAppSelector(selectSecurities));
  const securityChosen = plainToInstance(Security, useAppSelector(selectSecurityChosen));

  // Fetch the asset tyeps, to be called once, after rendering
  useEffect(() => {
    if (accessToken) {
      dispatch(fetchAssetTypes({ accessToken }));
    }
  });

  // Set filter to the remaining symbol if securities list contains only 1 security
  useEffect(() => {
    if (securities.length === 1 && securityChosen?.symbol !== securities[0].symbol) {
      const security = securities[0];
      dispatch(choose({ security: instanceToPlain(security) }));
      props.onSymbolChange(security.symbol);
      securityFilterRef.current?.blur();
      setSymbolFilter(security.symbol);
      setOpen(false);
    }
  }, [securities]);

  // Close searchbar when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSymbolFilter(securityChosen?.symbol ?? '');
        dispatch(resetSecurities());
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [securityChosen]);

  // Handle searchbar text change
  function onFilterChange(event: ChangeEvent<HTMLInputElement>): void {
    event.currentTarget.value = event.currentTarget.value.toUpperCase();
    const value = event.currentTarget.value;
    setSymbolFilter(value);
    if (symbolChangeTimer !== null) {
      clearTimeout(symbolChangeTimer);
    }
    setSymbolChangeTimer(setTimeout(() => {
      if (accessToken && value.length >= 3) {
        dispatch(fetchSecurities({ accessToken, symbolFilter: value, assetTypeIds }));
      } else {
        dispatch(resetSecurities());
      }
    }, 100));
  }

  // Handle searchbar arrowdown
  const onFilterKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      const element = securityListRef.current?.firstElementChild as HTMLElement;
      element?.focus();
    }
  }

  // Handle list symbol choice (mouse/touch)
  const onSecurityListClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    setSymbolFilter(e.currentTarget.innerText);
    const security = securities.find(s => s.symbol === e.currentTarget.innerText);
    if (security && security.id !== undefined) {
      dispatch(choose({ security: instanceToPlain(security) }));
      props.onSymbolChange(security.symbol);
      securityFilterRef.current?.blur();
      setOpen(false);
    }
    e.currentTarget?.blur();
  }
  // Handle list symbol choice (keyboard)
  const onSecurityListKeydown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter') {
      setSymbolFilter(e.currentTarget.innerText);
      const security = securities.find(s => s.symbol === e.currentTarget.innerText);
      if (security && security.id !== undefined) {
        dispatch(choose({ security: instanceToPlain(security) }));
        props.onSymbolChange(security.symbol);
        securityFilterRef.current?.blur();
        setOpen(false);
      }
      e.currentTarget?.blur();
    } else if (e.key === 'ArrowUp') {
      const element = document.activeElement?.previousElementSibling as HTMLElement;
      element?.focus();
      if (!element) {
        securityFilterRef?.current?.focus();
      }
    } else if (e.key === 'ArrowDown') {
      const element = document.activeElement?.nextElementSibling as HTMLElement;
      element?.focus();
    }
  }

  return (
    <div ref={wrapperRef} className={`group relative w-full lg:w-72 mx-10 cursor-pointer text-xl lg:text-2xl transition-color duration-200 ease-in
      ${open ? "text-neutral-600 hover:text-neutral-800 lg:text-3xl" : "text-neutral-300 hover:text-white lg:hover:text-3xl"}`}
    >
      <div
        onClick={() => { setOpen(true); securityFilterRef.current?.focus(); setSymbolFilter(''); }}
        onFocus={() => { setOpen(true); securityFilterRef.current?.focus(); setSymbolFilter(''); }}
        className={`
          relative flex items-center px-3 py-2 rounded-full overflow-hidden
          before:absolute before:top-0 before:bottom-0 before:right-0 before:rounded-full before:shadow
          before:transition-all before:duration-300 before:ease-in-out
          ${open ? "before:bg-neutral-200 before:w-full" : "bg-transparent before:w-0"}
        `}
      >
        {/* Input (always visible, above background) */}
        <input ref={securityFilterRef}
          onChange={onFilterChange}
          onKeyDown={onFilterKeydown}
          type="text"
          placeholder="procurar ação..."
          value={symbolFilter}
          className="relative z-10 pointer-events-none pl-2 w-full text-center bg-transparent border-0 outline-none"
        />

        {/* Icon (always visible, above background) */}
        <button className="relative z-10 mt-1 pointer-events-none">
          <IoSearch />
        </button>
      </div>

      <div className='relative w-full z-10'>
        <div className={`absolute top-0 left-0 right-0 p-1`}>
          <div className={`${symbolFilter.length > 0 && symbolFilter.length < 3 ? 'group-has-[:focus]:h-20 group-has-[:focus]:border group-has-[:focus]:ring-1 group-has-[:focus]:p-1' : 'border-0 ring-0'} h-0 overflow-hidden bg-neutral-200 rounded-md ring-black border-neutral-400 font-semibold text-gray-400 shadow-lg shadow-neutral-400/40 text-lg`}>
            Pelo menos 3 letras na busca
          </div>
        </div>
        <div className={`absolute top-0 left-0 right-0 p-1`}>
          <ul ref={securityListRef} className={`${securities.length > 0 ? 'group-has-[:focus]:h-36 group-has-[:focus]:border group-has-[:focus]:ring-1' : 'border-0 ring-0'} h-0 overflow-y-auto flex flex-col transition-all delay-0 duration-200 bg-white rounded-md ring-black border-neutral-400 shadow-lg shadow-neutral-400/40`}>
            {securities?.map(security =>
              <li tabIndex={0} onKeyDown={onSecurityListKeydown} onClick={onSecurityListClick} key={security.id} className='cursor-pointer rounded-md p-1 hover:bg-sky-100 focus:bg-sky-100 outline-none'>
                {security.symbol}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SymbolSearchBar;
